/**
 * Display formatters for workout list rows and prototypes.
 * Phase 3 inherits this; tweak cautiously — visual identity is the locked-in Phase 1 baseline.
 */
import type { Distance } from "@contract/types";

export function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const datePart = d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const timePart = d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${datePart}, ${timePart}`;
}

/** Date-only label for swim-log list rows — "Thu, Jun 18" (no clock time).
 *  Keeps each row to a single line at 390px. The aria-label on WorkoutRow
 *  continues to use formatDate (with the time) for screen-reader context. */
export function formatRowDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/** Day-level anchor for a swim: "Tuesday, Jun 11" (long weekday, no time).
 *  Shared by the Prototypes header, the Swim-tab ribbon subline, and the
 *  Swim/Club date-nav headers, so none of them can disagree about which day
 *  is on screen. When `iso` falls on `now`'s local calendar day, prefixes a
 *  quiet "Today · " marker ("Today · Wednesday, Jul 15") — readability round,
 *  2026-07-16: the date-nav header showed a bare weekday with no way to tell
 *  "today" from "any other day" at a glance. `now` is injectable for tests,
 *  same convention as formatHeroDay. */
export function formatWorkoutDay(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const label = d.toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  return isToday ? `Today · ${label}` : label;
}

/** The same day label with the weekday abbreviated ("Today · Wed, Sep 9"), for a date row
 *  too narrow for the full weekday (B-022, Scott 2026-09-13). "Today ·" and the full date stay. */
export function formatWorkoutDayShort(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const label = d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  return isToday ? `Today · ${label}` : label;
}

/** B-022: the full label when its one-line width fits the slot, else the short one (when there
 *  is one). Widths in CSS px, measured by DateNavHeader. */
export function chooseDateText(full: string, short: string | null | undefined, fullWidthPx: number, availablePx: number): string {
  return short && fullWidthPx > availablePx ? short : full;
}

/** The Club card's date line (B-031, build 14): the same format the date row shows on that
 *  screen, the short weekday when the row abbreviates, the full weekday otherwise. One rule. */
export function cardDayText(iso: string, now: Date, rowIsShort: boolean): string {
  return rowIsShort ? formatWorkoutDayShort(iso, now) : formatWorkoutDay(iso, now);
}

/** Hero-card date line (SwimHeroCard). A swim from TODAY reads
 *  "This morning · 6:02 am" — bucket from the SWIM's start hour (h < 12
 *  morning, h < 18 afternoon, else evening), am/pm LOWERCASED per the
 *  approved mock. Any other day delegates to formatWorkoutDay. `now` is
 *  injectable for tests; invalid dates return the iso unchanged (same
 *  convention as formatWorkoutDay). */
export function formatHeroDay(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (!sameDay) return formatWorkoutDay(iso, now);
  const h = d.getHours();
  const bucket = h < 12 ? "morning" : h < 18 ? "afternoon" : "evening";
  const time = d
    .toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    .toLowerCase();
  return `This ${bucket} · ${time}`;
}

/** Whole-second pace per 100m for the hero subline (SwimHeroCard).
 *  ROUNDED, not floored — the mock binds 2815s/1575m (178.73) to "2:59",
 *  which needs round(...)=179 BEFORE fmtPace formats (fmtMSS floors, so
 *  feeding it 178.73 would render "2:58"). Returns null (never NaN) for
 *  missing/zero/negative/non-finite distance or duration — the caller
 *  omits the pace segment entirely. */
export function pacePer100mSeconds(
  durationSeconds: number,
  totalDistance: Distance | null | undefined,
): number | null {
  if (
    !totalDistance ||
    !Number.isFinite(totalDistance.value) ||
    totalDistance.value <= 0
  ) {
    return null;
  }
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) return null;
  return Math.round((durationSeconds / totalDistance.value) * 100);
}

/** Hour-aware clock duration for the hero subline (SwimHeroCard).
 *  2815 → "46:55"; rolls to H:MM:SS at an hour (3725 → "1:02:05") with
 *  minutes zero-padded only in the hour form. formatDuration's "47 min"
 *  style is wrong for the mock; fmtMSS would render a 62-min swim as
 *  "62:05". null/negative/non-finite → "—". */
export function fmtClockDuration(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return "—";
  const total = Math.floor(seconds);
  const s = total % 60;
  const ss = s.toString().padStart(2, "0");
  if (total >= 3600) {
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    return `${h}:${m.toString().padStart(2, "0")}:${ss}`;
  }
  const m = Math.floor(total / 60);
  return `${m}:${ss}`;
}

export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  const totalMinutes = Math.round(seconds / 60);
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const remaining = totalMinutes % 60;
  return remaining === 0 ? `${hours}h` : `${hours}h ${remaining}m`;
}

export function formatDistance(d: { value: number; unit: string } | null): string {
  if (d === null) return "—";
  // Unified thousands-comma + single space before the unit ("6,050 m"). Unit is
  // preserved (a yard pool reads "2,100 yd") rather than hardcoded to meters.
  return `${Math.round(d.value).toLocaleString()} ${d.unit}`;
}

/**
 * Unified meters display across the History tab — ring center, trend caption,
 * and every "All swims" row distance. Thousands comma, single space before "m":
 * 6050 → "6,050 m", 2100 → "2,100 m". Rounds to a whole meter (the watch samples
 * lap distance, sub-meter precision is noise). Non-finite/negative → "—".
 */
export function fmtMeters(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value) || value < 0) {
    return "—";
  }
  return `${Math.round(value).toLocaleString()} m`;
}

export function formatActivity(t: string): string {
  // "swimming" -> "Swim"; pass through for unknowns.
  const map: Record<string, string> = {
    swimming: "Swim",
    running: "Run",
    cycling: "Bike",
    walking: "Walk",
  };
  return map[t] ?? t;
}

/** Format seconds as M:SS. */
export function fmtPace(seconds: number | null): string {
  return fmtMSS(seconds, ":");
}

/** Format seconds as M'SS"  (1'52" style — matches Scott's Workout screen). */
export function fmtPaceTick(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}'${s.toString().padStart(2, "0")}"`;
}

/** Format seconds as M{sep}SS — separator picks the visual style. */
export function fmtMSS(seconds: number | null, sep = ":"): string {
  if (seconds === null || !Number.isFinite(seconds)) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}${sep}${s.toString().padStart(2, "0")}`;
}

/**
 * One-decimal seconds for the set detail sheet — "26.1" (caller appends "s").
 * ONE decimal only, never two (two reads as false precision; the watch samples
 * lap time to ~0.1s, not finer). Rounds half AWAY FROM ZERO so 26.15 → 26.2 and
 * -26.15 → -26.2 (JS's banker-ish `toFixed` rounds 26.15 inconsistently across
 * engines; this is deterministic). Returns "—" for null/non-finite.
 *
 * Detail-sheet only — the ribbon stays label-only, and the story SENTENCES stay
 * digit-free (numbers live in the meta rows).
 */
export function fmtSeconds1(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return "—";
  const sign = seconds < 0 ? -1 : 1;
  const rounded = (sign * Math.round(Math.abs(seconds) * 10)) / 10;
  return rounded.toFixed(1);
}

/**
 * Clock time of day in 12-hour format for the set header: "9:14 AM".
 * No seconds. No leading zero on the hour. AM/PM uppercase.
 * Non-finite or negative ms → "—".
 */
export function fmtTimeOfDay(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  return new Date(ms).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/**
 * Rest gap in plain words for the set ribbon / detail sheet — no jargon, no raw
 * seconds. Under a minute reads "45 sec rest"; a minute or more rounds to the
 * nearest half-minute as "2½ min rest" (whole-minute halves drop the fraction).
 * Returns "" for null/non-finite so the caller can render nothing.
 */
export function fmtRest(ms: number | null, suffix = "rest"): string {
  if (ms === null || !Number.isFinite(ms) || ms <= 0) return "";
  const totalSeconds = ms / 1000;
  if (totalSeconds < 60) {
    const rounded = Math.max(5, Math.round(totalSeconds / 5) * 5);
    return `${rounded} sec ${suffix}`;
  }
  const halfMinutes = Math.round(totalSeconds / 30); // nearest half-minute
  const whole = Math.floor(halfMinutes / 2);
  const hasHalf = halfMinutes % 2 === 1;
  const minutePart = hasHalf ? `${whole}½` : `${whole}`;
  return `${minutePart} min ${suffix}`;
}
